import { html, nothing, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { OutlineImage } from '../../base/outline-image/outline-image';
import { classMap } from 'lit/directives/class-map.js';
import componentStyles from './ghs-image.css.lit';

type ImageVariantType = 'default' | 'slider';
type CaptionAlignmentType = 'right' | 'center' | 'left';

/**
 * The image component, with support for parallax scrolling.
 * @element ghs-image
 *
 * @slot default - The image to be presented.
 * @slot caption - The caption text for the image.
 */
@customElement('ghs-image')
export class GhsImage extends OutlineImage {
  static styles = [componentStyles];

  @state()
  hasSRDescription: boolean;

  /**
   * Whether or not the image has rounded borders.
   */
  @property({ type: Boolean }) rounded = false;

  /**
   * Whether or not the image renders with a default size.
   */
  @property({ type: Boolean, attribute: 'default-size', reflect: true })
  defaultSize = false;

  /**
   * Whether or not the image has a full height.
   */
  @property({ type: Boolean, attribute: 'full-height', reflect: true })
  fullHeight = false;

  /**
   * Whether or not this image has a shadow.
   */
  @property({ type: Boolean, attribute: 'has-shadow' }) hasShadow = false;

  /**
   * Image variant type.
   */
  @property() variant: ImageVariantType = 'default';

  /**
   * Caption alignment.
   */
  @property({ type: String, attribute: 'caption-alignment', reflect: true })
  captionAlignment: CaptionAlignmentType = 'left';

  /**
   * Ignore image ratio for mobile.
   */
  @property({ type: Boolean, attribute: 'sideways', reflect: true })
  sideways: false;

  firstUpdated(): void {
    super.firstUpdated();

    this.hasSRDescription =
      this.querySelector('[slot="srdescription"]') !== null;
  }

  captionSlotTemplate(): TemplateResult | null {
    return this.hasCaptionSlot || this.hasSRDescription
      ? html`
          <figcaption class="align-${this.captionAlignment}">
            ${this.hasCaptionSlot
              ? html` <slot name="caption"></slot>`
              : nothing}
            ${this.hasSRDescription
              ? html`<slot name="srdescription"></slot>`
              : nothing}
          </figcaption>
        `
      : null;
  }

  render(): TemplateResult {
    const classes = {
      [`image--${this.variant}`]: this.variant,
      'rounded': this.rounded,
      'has-shadow': this.hasShadow,
      'full-height': this.fullHeight,
      'sideways': this.sideways,
      'default-size': this.defaultSize,
      [`align-${this.captionAlignment}`]: this.captionAlignment,
    };

    return html`
      <figure class="${classMap(classes)}">
        <slot></slot>
        ${this.captionSlotTemplate() ?? nothing}
      </figure>
    `;
  }
}
